import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import SequenceUpload from '@/components/sequences/SequenceUpload';
import BulkUploadSection from '@/components/sequences/BulkUploadSection';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Info, Music, FolderOpen, X, Upload, CheckCircle, FolderCheck, Search, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WIZARD_STEPS } from '@/components/sequences/upload/types';

const UploadSequencePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [uploadType, setUploadType] = useState<'individual' | 'bulk'>('individual');
  const [showAlert, setShowAlert] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  // Check if alert was previously dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('spotify-alert-dismissed');
    if (dismissed === 'true') {
      setShowAlert(false);
    }
  }, []);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/auth?redirect=/upload-sequence');
    }
  }, [user, navigate]);

  const handleUploadSuccess = (sequenceId: string) => {
    navigate(`/sequence/${sequenceId}`);
  };

  const handleCancel = () => {
    navigate('/sequences');
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const dismissAlert = () => {
    setShowAlert(false);
    localStorage.setItem('spotify-alert-dismissed', 'true');
  };


  const handleBulkUploadStart = (files: FileList) => {
    console.log('Bulk upload started with files:', files);
  };

  const handleBulkUploadComplete = (sessionId: string) => {
    console.log('Bulk upload completed with session ID:', sessionId);
    // You might want to navigate to a status page or show a success message
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      
      <main className="container mx-auto px-4 pt-24 pb-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Upload Your Sequences
            </h1>
            <p className="text-gray-600">
              Share your light sequences with the community and start earning
            </p>
          </div>

          {/* Enhanced Alert with Dismiss */}
          {showAlert && (
            <Card className="bg-blue-50 border-blue-200 relative">
              <CardContent className="p-6">
                <Alert className="border-0 bg-transparent">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="pr-8">
                    <span className="font-semibold text-blue-900">New! Enhanced Upload Process</span>
                    <br />
                    <span className="text-blue-800">
                      We've enhanced the upload process with Spotify integration. 
                      Search for songs to automatically populate metadata like artist, album, and album artwork. 
                      This helps buyers find your sequences more easily and provides better organization.
                    </span>
                  </AlertDescription>
                </Alert>
                <button
                  onClick={dismissAlert}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded p-1 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          )}

          {/* Upload Type Selection */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card 
              className={cn(
                "cursor-pointer transition-all hover:shadow-lg hover:border-primary/50",
                uploadType === 'individual' && "border-primary shadow-lg ring-2 ring-primary/20"
              )}
              onClick={() => setUploadType('individual')}
            >
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Music className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Individual Sequence</h3>
                <p className="text-muted-foreground mb-4">
                  Upload a single sequence file with full control over listing details
                </p>
                <ul className="text-sm text-left space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Full control over pricing and description</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Immediate listing after upload</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Perfect for custom sequences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Spotify integration for metadata</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card 
              className={cn(
                "cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 relative",
                uploadType === 'bulk' && "border-primary shadow-lg ring-2 ring-primary/20"
              )}
              onClick={() => setUploadType('bulk')}
            >
              <div className="absolute -top-2 right-4 bg-linear-to-r from-amber-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                White Glove
              </div>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <FolderOpen className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Full Show Upload</h3>
                <p className="text-muted-foreground mb-4">
                  Upload your entire Christmas/Halloween show folder - we'll handle the rest!
                </p>
                <ul className="text-sm text-left space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Upload entire show directories at once</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Professional organization by our team</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Optimized listings with proper metadata</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>Save hours of individual uploads</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <span>24-48 hour processing time</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Individual Upload Section */}
          {uploadType === 'individual' && (
            <div className="grid gap-6 lg:grid-cols-4">
              {/* Steps Sidebar */}
              <div className="lg:col-span-1">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4">Upload Process</h3>
                    <div className="space-y-4">
                      {WIZARD_STEPS.map((step, index) => (
                        <div key={step.id} className="flex gap-3">
                          <div
                            className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                              index === currentStep
                                ? "border-primary bg-primary text-white"
                                : index < currentStep
                                ? "border-green-600 bg-green-600 text-white"
                                : "border-gray-300 bg-muted text-muted-foreground"
                            )}
                          >
                            {index < currentStep ? "✓" : index + 1}
                          </div>
                          <div>
                            <h4 className="font-medium text-sm">{step.label}</h4>
                            <p className="text-xs text-muted-foreground">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-medium text-sm mb-2">Supported Formats</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• xLights (.fseq)</li>
                        <li>• Light-O-Rama (.lms, .seq)</li>
                        <li>• Vixen (.xml)</li>
                        <li>• Zip archives (.zip)</li>
                      </ul>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium text-sm mb-2">Guidelines</h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• Max file size: 50MB</li>
                        <li>• Include detailed descriptions</li>
                        <li>• Use accurate song information</li>
                        <li>• Price fairly for the community</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Upload Form */}
              <div className="lg:col-span-3">
                <SequenceUpload
                  onSuccess={handleUploadSuccess}
                  onCancel={handleCancel}
                  onStepChange={handleStepChange}
                />
              </div>
            </div>
          )}

          {/* Bulk Upload Section */}
          {uploadType === 'bulk' && (
            <BulkUploadSection
              onUploadStart={handleBulkUploadStart}
              onUploadComplete={handleBulkUploadComplete}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UploadSequencePage; 